import React, { useState } from "react";
import {
  Box,
  Flex,
  Text,
  useColorModeValue,
  SimpleGrid,
  Icon,
  useToast,
  Spinner,
} from "@chakra-ui/react";
import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import {
  FaSyncAlt,
  FaDownload,
  FaCloudUploadAlt,
  FaDollarSign,
} from "react-icons/fa";

import SettingApi from "api/setting";

const Settings = () => {
  const [loading, setLoading] = useState(false);
  const textColor = useColorModeValue("gray.700", "white");
  const boxBg = useColorModeValue("white", "gray.800");
  const cardShadow = useColorModeValue("md", "dark-lg");
  const descColor = useColorModeValue("gray.500", "gray.400");
  const toast = useToast();

  const updateSitesStock = async () => {
    setLoading(true);
    try {
      const res = await SettingApi.UpdateSitesStock();
      toast({
        title: "موفق!",
        description: res.data.message,
        status: "success",
        duration: 2000,
        isClosable: true,
        position: "top-right",
      });
    } catch (err) {
      toast({
        title: "خطا",
        description:
          err.response?.data?.message || "مشکلی در به‌روزرسانی سایت‌ها پیش آمد.",
        status: "error",
        duration: 2000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setLoading(false);
    }
  };

  const updateSystemStock = async () => {
    setLoading(true);
    try {
      const res = await SettingApi.UpdateSystemStock();
      toast({
        title: "موفق!",
        description: res.data.message,
        status: "success",
        duration: 2000,
        isClosable: true,
        position: "top-right",
      });
    } catch (err) {
      toast({
        title: "خطا",
        description:
          err.response?.data?.message || "مشکلی در به‌روزرسانی سیستم پیش آمد.",
        status: "error",
        duration: 2000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setLoading(false);
    }
  };

  const importProducts = async () => {
    setLoading(true);
    try {
      const res = await SettingApi.ImportProducts();
      const products = res.data?.data?.products;

      if (products && products.length > 0) {
        const productNames = products.map((product) => (
          <Text key={product.name} color={textColor}>{product.name}</Text>
        ));

        toast({
          title: "محصولات اضافه شدند",
          description: (
            <Box>
              <Text>محصولات زیر با موفقیت وارد شدند:</Text>
              {productNames}
            </Box>
          ),
          status: "success",
          duration: 5000,
          isClosable: true,
          position: "top-right",
        });
      } else {
        toast({
          title: "خطا",
          description: "هیچ محصولی برای وارد کردن پیدا نشد.",
          status: "error",
          duration: 2000,
          isClosable: true,
          position: "top-right",
        });
      }
    } catch (err) {
      toast({
        title: "خطا",
        description:
          err.response?.data?.message || "مشکلی در وارد کردن محصولات پیش آمد.",
        status: "error",
        duration: 2000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setLoading(false);
    }
  };

  const updateSystemPrice = async () => {
    setLoading(true);
    try {
      const res = await SettingApi.UpdateSystemPrice();
      toast({
        title: "موفق!",
        description: res.data.message,
        status: "success",
        duration: 2000,
        isClosable: true,
        position: "top-right",
      });
    } catch (err) {
      toast({
        title: "خطا",
        description:
          err.response?.data?.message || "مشکلی در به‌روزرسانی قیمت سیستم پیش آمد.",
        status: "error",
        duration: 2000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setLoading(false);
    }
  };

  const actionBoxes = [
    {
      title: "به‌روزرسانی موجودی سایت‌ها",
      description: "ارسال موجودی از سیستم به سایت‌ها",
      icon: FaCloudUploadAlt,
      color: "teal.500",
      onClick: updateSitesStock,
    },
    {
      title: "به‌روزرسانی موجودی سیستم",
      description: "به‌روزرسانی موجودی سیستم",
      icon: FaSyncAlt,
      color: "orange.400",
      onClick: updateSystemStock,
    },
    {
      title: "وارد کردن محصولات",
      description: "بارگذاری محصولات جدید از سایت به سیستم",
      icon: FaDownload,
      color: "blue.400",
      onClick: importProducts,
    },
    {
      title: "به‌روزرسانی قیمت سیستم",
      description: "دریافت و به‌روزرسانی قیمت محصولات از فارست",
      icon: FaDollarSign,
      color: "green.400",
      onClick: updateSystemPrice,
    },
  ];

  return (
    <Flex direction="column" pt={{ base: "120px", md: "75px" }}>
      <Card>
        <CardHeader
          p="6px 0px 22px 0px"
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
          <Flex align="center" gap={2}>
            <FaCloudUploadAlt size={20} />
            <Text fontSize="xl" color={textColor} fontWeight="bold">
              عملیات سیستم محصولات
            </Text>
          </Flex>
        </CardHeader>

        <Box px={6} pb={6}>
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
          {actionBoxes.map((box, idx) => (
            <Box
              key={idx}
              p={6}
              bg={boxBg}
              borderRadius="xl"
              boxShadow={cardShadow}
              cursor="pointer"
              _hover={{
                boxShadow: "lg",
                transform: "translateY(-4px)",
              }}
              transition="all 0.2s ease-in-out"
              onClick={box.onClick}
              pointerEvents={loading ? "none" : "auto"}
            >
              <Flex direction="column" align="center" textAlign="center">
                <Icon as={box.icon} boxSize={8} mb={3} color={box.color} />
                <Text fontWeight="bold" fontSize="lg" mb={1} color={textColor}>
                  {box.title}
                </Text>
                <Text fontSize="sm" color={descColor}>
                  {box.description}
                </Text>
              </Flex>
            </Box>
          ))}
        </SimpleGrid>
        </Box>
      </Card>

      {loading && (
        <Flex
          position="fixed"
          top="0"
          left="0"
          right="0"
          bottom="0"
          bg="rgba(0, 0, 0, 0.4)"
          zIndex="9999"
          align="center"
          justify="center"
          pointerEvents="none"
        >
          <Spinner size="xl" color="white" />
        </Flex>
      )}
    </Flex>
  );
};

export default Settings;
